import { createState } from "@tecnobit-srl/state";
import axios from "axios";
import Echo from "laravel-echo";
import Pusher from "pusher-js";
import { useCallback, useEffect, useRef, useState } from "react";
import { jsx } from "react/jsx-runtime";
//#region src/index.tsx
var isBrowser = typeof window !== "undefined";
function readCookie(name) {
	if (!isBrowser) return null;
	const match = new RegExp(`(?:^|; )${name}=([^;]*)`).exec(document.cookie);
	return match ? decodeURIComponent(match.at(1) ?? "") : null;
}
/** Read the per-site public Reverb config the bridge shared with the current page. */
function resolveReverbConfig() {
	if (!isBrowser) return null;
	return window.inertiaPage?.reverb ?? null;
}
var instance = null;
/**
* Lazily create the single browser Echo/pusher-js client. Returns null during SSR
* (typeof window === 'undefined') or when the site has no Reverb config — so
* entry.server.tsx never opens a socket and unconfigured sites stay inert.
*/
function getEcho(config) {
	if (!isBrowser) return null;
	if (instance) return instance;
	const cfg = config ?? resolveReverbConfig();
	if (!cfg?.key) return null;
	window.Pusher = Pusher;
	instance = new Echo({
		broadcaster: "reverb",
		key: cfg.key,
		wsHost: cfg.host,
		wsPort: cfg.port,
		wssPort: cfg.port,
		forceTLS: cfg.scheme === "https",
		enabledTransports: ["ws", "wss"],
		wsPath: cfg.path,
		authorizer: (channel) => ({ authorize: (socketId, callback) => {
			axios.post("/broadcasting/auth", {
				socket_id: socketId,
				channel_name: channel.name
			}, { headers: { "X-CSRF-TOKEN": readCookie("XSRF-TOKEN") ?? "" } }).then((response) => {
				callback(null, response.data);
			}).catch((error) => {
				callback(error instanceof Error ? error : /* @__PURE__ */ new Error("Broadcast channel authorization failed"), null);
			});
		} })
	});
	return instance;
}
/** Disconnect and drop the client (e.g. on logout). */
function disconnectEcho() {
	instance?.disconnect();
	instance = null;
}
/** Subscribe to a channel by visibility (private/presence trigger the auth POST). */
function subscribeChannel(echo, name, visibility) {
	if (visibility === "private") return echo.private(name);
	if (visibility === "presence") return echo.join(name);
	return echo.channel(name);
}
var { ContextProvider, useStore } = createState("Echo", (set, get) => ({
	echo: null,
	ensureConnected: () => {
		const existing = get().echo;
		if (existing) return existing;
		const echo = getEcho();
		if (echo) set({ echo });
		return echo;
	}
}));
/**
* Scoped Echo store provider. Connection is LAZY — no socket is opened until a
* component actually subscribes (useChannel/useEchoEvent), so mounting this high in
* the tree is free and SSR-safe.
*/
function EchoProvider({ children }) {
	return /* @__PURE__ */ jsx(ContextProvider, { children });
}
function useEcho(selector, equalityFn) {
	return useStore(selector, equalityFn);
}
/**
* Subscribe to a channel post-hydration and return it (null before the client is
* ready). Leaves the channel on unmount. Public channels connect directly; private
* and presence trigger the /broadcasting/auth round-trip via the axios authorizer.
*/
function useChannel(name, visibility = "public") {
	const ensureConnected = useStore((s) => s.ensureConnected);
	const [channel, setChannel] = useState(null);
	useEffect(() => {
		if (!name) return;
		const echo = ensureConnected();
		if (!echo) return;
		setChannel(subscribeChannel(echo, name, visibility));
		return () => {
			echo.leave(name);
			setChannel(null);
		};
	}, [
		ensureConnected,
		name,
		visibility
	]);
	return channel;
}
/**
* Bind a handler to an event on a channel for the component's lifetime.
*/
function useEchoEvent(channelName, event, handler, visibility = "public") {
	const channel = useChannel(channelName, visibility);
	useEffect(() => {
		if (!channel) return;
		const listener = (payload) => {
			handler(payload);
		};
		channel.listen(event, listener);
		return () => {
			channel.stopListening(event, listener);
		};
	}, [
		channel,
		event,
		handler
	]);
}
/**
* Must stay in sync with `DataInvalidated::broadcastAs()` in
* packages/tecnobit-cms-core/TecnobitCore/Broadcasting/Events/DataInvalidated.php.
* Echo namespaces custom broadcastAs() events unless the listener name starts with
* a dot, so the subscribed form is `.data.invalidated` — encapsulated here so no
* caller ever writes it by hand.
*/
var DATA_INVALIDATED_EVENT = "data.invalidated";
var DATA_INVALIDATED_LISTENER = `.${DATA_INVALIDATED_EVENT}`;
/**
* Must stay in sync with `DataInvalidated::SITE_CHANNEL` and
* `DataInvalidated::SITE_SCOPE` in
* packages/tecnobit-cms-core/TecnobitCore/Broadcasting/Events/DataInvalidated.php.
* The public channel carrying site-global invalidations (settings, menus, the
* route table) — already per-site because each instance runs its own Reverb
* application — and the well-known scope those invalidations use, the slices
* being what carries which entity changed.
*/
var SITE_CHANNEL = "site";
var SITE_SCOPE = "site:global";
/**
* Long enough to collapse a burst (several lessons graded together), short enough
* to feel instant. Confirm against real EasyGenerator grading behaviour.
*/
var DEBOUNCE_MS = 300;
function resolveUserId() {
	if (!isBrowser) return null;
	return window.inertiaPage?.user?.id ?? null;
}
/**
* Subscribe to live-data invalidations for the given (slices, scope) pair.
*
* Delivery is over the user's own private `users.{userId}` channel by default,
* or over the public `site` channel for site-global slices (via the `channel`
* option); the `visibilitychange` fallback is registered unless opted out, so
* a site without Reverb, an unreachable socket, or a proxy that blocks
* WebSockets still refreshes when the page becomes visible again. The fallback
* keys on visibility (not focus), so it never fires on initial load nor on
* focus changes that never hid the page.
*
* Inert during SSR: effects never run server-side, so no client is created and no
* listener is registered. Everything is released on unmount.
*/
function useDataInvalidation({ slices, scope, refresh, channel, visibilityFallback = true }) {
	const slicesRef = useRef(slices);
	const refreshRef = useRef(refresh);
	useEffect(() => {
		slicesRef.current = slices;
		refreshRef.current = refresh;
	});
	const inFlightRef = useRef(false);
	const pendingRefreshRef = useRef(false);
	const mountedRef = useRef(true);
	const timerRef = useRef(null);
	const scheduleRefresh = useCallback(() => {
		if (inFlightRef.current) {
			pendingRefreshRef.current = true;
			return;
		}
		if (timerRef.current !== null) clearTimeout(timerRef.current);
		timerRef.current = setTimeout(() => {
			timerRef.current = null;
			if (inFlightRef.current) {
				pendingRefreshRef.current = true;
				return;
			}
			inFlightRef.current = true;
			Promise.resolve().then(() => refreshRef.current()).catch(() => void 0).finally(() => {
				inFlightRef.current = false;
				if (mountedRef.current && pendingRefreshRef.current) {
					pendingRefreshRef.current = false;
					scheduleRefresh();
				}
			});
		}, DEBOUNCE_MS);
	}, []);
	useEffect(() => {
		if (!isBrowser) return;
		mountedRef.current = true;
		const onVisibilityChange = () => {
			if (document.visibilityState === "visible") scheduleRefresh();
		};
		if (visibilityFallback) document.addEventListener("visibilitychange", onVisibilityChange);
		return () => {
			mountedRef.current = false;
			if (visibilityFallback) document.removeEventListener("visibilitychange", onVisibilityChange);
			pendingRefreshRef.current = false;
			if (timerRef.current !== null) {
				clearTimeout(timerRef.current);
				timerRef.current = null;
			}
		};
	}, [scheduleRefresh, visibilityFallback]);
	const handler = useCallback((payload) => {
		if (payload.scope !== scope) return;
		if (!payload.slices.some((slice) => slicesRef.current.includes(slice))) return;
		scheduleRefresh();
	}, [scope, scheduleRefresh]);
	const userId = isBrowser ? resolveUserId() : null;
	let channelName = channel?.name ?? null;
	if (!channel && userId !== null) channelName = `users.${String(userId)}`;
	useEchoEvent(channelName, DATA_INVALIDATED_LISTENER, handler, channel?.visibility ?? "private");
}
//#endregion
export { DATA_INVALIDATED_EVENT, EchoProvider, SITE_CHANNEL, SITE_SCOPE, disconnectEcho, getEcho, resolveReverbConfig, useChannel, useDataInvalidation, useEcho, useEchoEvent };
